import { TZDate, tz, tzScan } from "@date-fns/tz";
import { Duration, format, isBefore, sub } from "date-fns";
import { useCallback } from "react";
import { DEFAULT_DECIMAL_SEPARATOR } from "../constants";
import { getPhoneCountryCode } from "../schemas/phoneFormSchema";

type Options = {
  currency?: string;
  reverse?: boolean;
  includeRest?: boolean;
  defaultValue?: string;
};

const ISO_DURATION_REGEX =
  /P(?:([\d]+\.?[\d]*|\.[\d]+)Y)?(?:([\d]+\.?[\d]*|\.[\d]+)M)?(?:([\d]+\.?[\d]*|\.[\d]+)W)?(?:([\d]+\.?[\d]*|\.[\d]+)D)?(?:T(?:([\d]+\.?[\d]*|\.[\d]+)H)?(?:([\d]+\.?[\d]*|\.[\d]+)M)?(?:([\d]+\.?[\d]*|\.[\d]+)S)?)?$/;

export function getCurrency(countryCode?: string) {
  switch (countryCode) {
    case "NO":
      return "NOK";
    default:
      return "PLN";
  }
}

export function getVATByCountryCode(countryCode?: string) {
  switch (countryCode) {
    case "NO":
      return "25";
    default:
      return "23";
  }
}

export function getInsuranceByCountryCode(countryCode?: string) {
  switch (countryCode) {
    case "NO":
      return "10 000 000 kr";
    default:
      return "1 000 000 PLN";
  }
}

export function formatPrice(countryCode?: string) {
  return useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO
    (price?: Maybe<number | string>, options?: Options): string => {
      const currency = options?.currency || getCurrency(countryCode);

      if (typeof price === "string" || typeof price === "number") {
        const value = (typeof price === "string" ? Number.parseInt(price) : price) / 100;

        if (options?.reverse) {
          return Number.isNaN(value) ? "" : value.toFixed(0);
        }

        const parsedPrice = value.toFixed(2).toString().replace(".", DEFAULT_DECIMAL_SEPARATOR);
        const formattedPrice =
          parsedPrice.includes(`${DEFAULT_DECIMAL_SEPARATOR}00`) && !options?.includeRest
            ? Number.parseInt(parsedPrice)
            : parsedPrice;

        return `${formattedPrice} ${currency}`;
      }

      return options?.defaultValue || "";
    },
    [countryCode],
  );
}

export function phoneWithCountryCode(phoneNumber: string, countryCode: string): string {
  const phoneCountryCode = getPhoneCountryCode(countryCode);

  return `${phoneCountryCode}${phoneNumber.replace(phoneCountryCode, "")}`;
}

export function personName(firstName: string, lastName: string) {
  const userLastName = lastName.length > 0 ? `${lastName[0]}.` : "";
  return `${firstName} ${userLastName}`;
}

export function twoDigitFormat(value: number | string) {
  const intValue = typeof value === "string" ? Number.parseInt(value) : value;

  return intValue > 9 ? `${intValue}` : `0${intValue}`;
}

export function formatDraftReservationDate(
  reservationStartDate?: string | null,
  subscriptionStartDate?: string,
) {
  const referenceStartDate = subscriptionStartDate || reservationStartDate;

  if (!referenceStartDate || !reservationStartDate) return "";

  const offset = format(referenceStartDate, "xxx", { in: tz("Europe/Warsaw") });

  const date = `${format(reservationStartDate, "yyyy-MM-dd'T'HH:mm:ss")}${offset}`;
  return date;
}

export function formatReservationOffset(reservationDate: string, subscriptionStartDate?: string) {
  if (!reservationDate) return "";

  if (subscriptionStartDate) {
    const scan = tzScan("Europe/Warsaw", {
      start: new TZDate(subscriptionStartDate || "", "Europe/Warsaw"),
      end: new TZDate(reservationDate || "", "Europe/Warsaw"),
    });

    const totalChange = scan?.reduce((acc, cur) => {
      if (isBefore(new TZDate(reservationDate || ""), cur?.date)) return acc;

      acc += cur?.change;

      return acc;
    }, 0);

    const formattedDate = sub(reservationDate, { minutes: totalChange }).toISOString();

    return formattedDate;
  }

  return reservationDate;
}

export function formatReservationDate(
  reservationDate: string | Date,
  formatType: string,
  subscriptionStartDate?: string,
) {
  if (!reservationDate) return "";

  const date = new Date(reservationDate);

  // biome-ignore lint/suspicious/noGlobalIsNan: skip
  if (isNaN(date.getTime())) {
    return "";
  }

  const newDate = date?.toISOString();

  const dateWithOffset = formatReservationOffset(newDate, subscriptionStartDate);

  return format(dateWithOffset, formatType, { in: tz("Europe/Warsaw") });
}

export function parseISODuration(argument: string): Duration {
  if (argument === "P0D" || argument === "0") return { hours: 0, minutes: 0 };

  const parsedArgument = argument.replace(/,/g, "."); // Decimal fraction may be specified with either a comma or a full stop

  const match = parsedArgument.match(ISO_DURATION_REGEX);

  if (!match) {
    throw new RangeError("Invalid format");
  }

  const [, years = 0, months = 0, weeks = 0, days = 0, hours = 0, minutes = 0, seconds = 0] = match;

  const entries = Object.entries({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
  }) as [keyof Duration, string][];

  return entries.reduce<Duration>((prev, [key, value]) => {
    prev[key] = +value;
    return prev;
  }, {});
}

export function formatDurationToTime(timeConsumption: string): string {
  const totalMinutes = formatDurationToMinutes(timeConsumption);

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  const cleaningTime =
    totalHours >= 1
      ? remainingMinutes === 0
        ? `${totalHours}h`
        : `${totalHours}h ${remainingMinutes}min`
      : remainingMinutes > 1
        ? `${remainingMinutes}min`
        : "";

  return cleaningTime;
}

export function formatDurationToMinutes(duration: string) {
  const {
    years = 0,
    months = 0,
    weeks = 0,
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
  } = parseISODuration(duration || "P0D");

  const totalMinutes =
    +years * 525600 + // 365 days in a year * 24 hours * 60 minutes
    +months * 43800 + // 30.44 days in a month * 24 hours * 60 minutes (average)
    +weeks * 10080 + // 7 days in a week * 24 hours * 60 minutes
    +days * 1440 + // 24 hours * 60 minutes
    +hours * 60 + // 60 minutes
    +minutes + // remaining minutes
    +seconds / 60; // convert seconds to minutes

  return totalMinutes;
}

export function formatDurationToDays(duration: string): number {
  const { years = 0, months = 0, weeks = 0, days = 0 } = parseISODuration(duration || "P0D");

  const totalDays =
    years * 365 + // 1 year = 365 days (not accounting for leap years)
    months * 30 + // 1 month = 30 days
    weeks * 7 + // 1 week = 7 days
    days;

  return totalDays;
}

export function formatDurationToMonths(duration: string) {
  const { years = 0, months = 0, weeks = 0, days = 0 } = parseISODuration(duration || "P0D");

  const totalMonths =
    years * 12 + // 1 year = 12 months
    months + // Number of months
    Math.floor(weeks / 4) + // Average of 4 weeks per month
    Math.floor(days / 30); // Average of 30 days per month

  return totalMonths;
}
